import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography, IconButton
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getReadyToCollect, markRepairCollected, deleteRepair } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { canDelete } from '../../utils/auth';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDateTime } from '../../utils/tableExport';

const ReadyToCollect = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getReadyToCollect();
      setRows(res.data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load ready-to-collect jobs'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDone = async (row) => {
    if (!window.confirm(`Mark JOB ${row.grn_number || row.service_number} as Closed Job (customer collected)?`)) return;
    try {
      setBusyId(row.id);
      await markRepairCollected(row.id);
      setSuccess('Moved to Closed Job');
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to mark collected'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!canDelete()) return;
    const label = row.grn_number || row.service_number || `#${row.id}`;
    if (!window.confirm(`Delete job ${label}? This cannot be undone.`)) return;
    try {
      setBusyId(row.id);
      await deleteRepair(row.id);
      setSuccess(`Deleted ${label}`);
      load();
    } catch (err) {
      setError(getErrorMessage(err, 'Delete failed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>Ready to Collect</Typography>
          <Typography variant="body2" color="text.secondary">
            Completed repairs waiting for customer collection.
          </Typography>
        </Box>
        <ExportButtons
          title="Ready to Collect"
          filename="ready_to_collect"
          columns={[
            { label: 'JOB #', value: (r) => dash(r.grn_number || r.service_number) },
            { label: 'Customer', value: (r) => dash(r.customer_name) },
            { label: 'Phone', value: (r) => dash(r.customer_phone) },
            { label: 'Device', value: (r) => [r.device_brand, r.device_model].filter(Boolean).join(' ') || '-' },
            { label: 'Technician', value: (r) => (
              r.repair_location_type === 'outsource' || r.repair_location_type === 'both'
                ? dash(r.outsource_technician_name)
                : dash(r.technician_name || r.first_inspection_technician_name)
            ) },
            { label: 'Ready since', value: (r) => fmtExportDateTime(r.ready_for_collection_at) },
            { label: 'Notes', value: (r) => (r.customer_rejected_quotation ? 'Rejected / not repaired' : '-') }
          ]}
          rows={rows}
          onError={setError}
        />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>JOB #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Technician</TableCell>
                <TableCell>Ready since</TableCell>
                <TableCell>Action</TableCell>
                {canDelete() && <TableCell>Delete</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canDelete() ? 7 : 6} align="center">No jobs ready for collection</TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Chip
                      label={r.grn_number || r.service_number}
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/repairs/${r.id}`)}
                      sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>{r.customer_name}<br /><Typography variant="caption">{r.customer_phone}</Typography></TableCell>
                  <TableCell>{r.device_brand} {r.device_model}</TableCell>
                  <TableCell>
                    {r.repair_location_type === 'outsource' || r.repair_location_type === 'both'
                      ? r.outsource_technician_name
                      : r.technician_name || r.first_inspection_technician_name || '-'}
                    {r.customer_rejected_quotation && (
                      <Chip size="small" color="error" label="Rejected / not repaired" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {r.ready_for_collection_at
                      ? new Date(r.ready_for_collection_at).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={busyId === r.id}
                      onClick={() => handleDone(r)}
                    >
                      {busyId === r.id ? '...' : 'Closed Job'}
                    </Button>
                  </TableCell>
                  {canDelete() && (
                    <TableCell>
                      <IconButton size="small" color="error" disabled={busyId === r.id} onClick={() => handleDelete(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ReadyToCollect;
